<template>
  <section class="app-body researcher">
    <template v-if="$route.query.fullName && !isLoaded">
      <!--
        If fullName is set for the query, we come from the search filter author redirect.
        Show a more generic preloader then, since we don't know if this author has a profile
      -->
      <spinner :containerClass="`width--50px min-height--150px mt-4`" />
    </template>
    <template v-else>
      <!-- render researcher header -->
      <researcher-header :user="user" :researcher="researcher" :isLoaded="isLoaded" :hasResearch="hasResearch" />

      <!-- render static messages -->
      <div v-if="error" class="container-fluid mt-5 mb-0">
        <static-messages v-if="error" :messages="error" :variant="`danger`" :containerClass="`mb-0`" @closed="error = null"/>
      </div>

      <!-- render template -->
      <router-view
        :user="user"
        :searchFilterObject="searchFilterObject"
        :researcher="researcher"
        :isLoaded="isLoaded"
        :hasResearch="hasResearch"
        :indexes="indexes"
      />
    </template>
  </section>
</template>

<script>
import ResearcherService from '@/services/researcher'
import SearchService from '@/services/search'
import Spinner from '@/components/shared/Spinner'

import ResearcherHeader from '@/components/researcher/Header'
import StaticMessages from '@/components/shared/StaticMessages'
import MPService from '@/services/mixpanel'
import highlights from '@/mixins/highlights'

export default {
  name: 'researcherBase',
  mixins: [highlights],
  components: {
    ResearcherHeader,
    StaticMessages,
    Spinner
  },
  data () {
    return {
      error: null,
      isLoaded: false,
      user: {},
      researcher: {},
      hasResearch: false,
      indexes: {},
      objectForHighlighting: {
        nameProperties: ['full_name', 'initial_name'],
        properties: ['about'],
        arrays: ['organisations.name']
      },
      suggestionIndexes: ['projects', 'publications', 'clinical_trials', 'patents']
    }
  },
  computed: {
    searchFilterObject () {
      return {
        key: 'researchers',
        values: '',
        index: 'publications',
        global: false,
        suggestions: []
      }
    }
  },
  beforeRouteUpdate (to, from, next) {
    let isPublicParam = false
    // Reset public param when going to a scoped search
    if (!to.meta.searchContainer) {
      if (from.query.public && !to.query.public) isPublicParam = true
      if (from.path === to.path) isPublicParam = false
    }
    if (to.params.id !== from.params.id || to.query.public !== from.query.public) this.fetchResearcher(to.params.id)
    this.setAsPublic(to.query.public)
    // fetch new profile
    this.checkOpenModal(to.query)
    // add correct param
    return !isPublicParam ? next() : next({ name: to.name, params: to.params, query: { public: true } })
  },
  beforeRouteLeave (to, from, next) {
    if (from.query.public) this.setAsPublic(false)
    next()
  },
  metaInfo () {
    return {
      titleTemplate: this.isLoaded ? `${this.getFullName(this.researcher)} | AcademicLabs` : `%s`,
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content: this.researcher.about ? this.stripTags(this.researcher.about) : ''
        }
      ]
    }
  },
  created () {
    this.setAsPublic(this.$route.query.public)
    this.fetchResearcher(this.$route.params.id)

    // error handling for connect
    if (this.$ls.getRegisterData().error) {
      this.error = this.$ls.getRegisterData().error
      this.$ls.removeRegisterData()
    }
  },
  mounted () {
    this.$events.$on('user:updated', (key, value) => {
      if (this.user.hasOwnProperty(key)) this.$set(this.user, key, value)
    })

    this.$events.$on('researcher:updated', (key, value) => {
      if (this.researcher.hasOwnProperty(key)) {
        this.$set(this.researcher, key, value)
        for (let esProperty of this.objectForHighlighting.nameProperties) this.$set(this.researcher.highlights, esProperty, '')
        if (this.objectForHighlighting.properties.includes(key)) this.$set(this.researcher.highlights, key, value)
      }
    })

    // We use the researcher ID to bookmark researchers, but at this data level we get the bookmark_folders on the user object
    // So we kind of have to combine this logic for now
    this.$events.$on('bookmark_folder:bookmarked', (bookmarkFolders, index, itemID) => {
      if (index === 'researcher' && itemID === this.researcher.id) {
        this.user.bookmark_folders = bookmarkFolders
      }
    })

    this.$events.$on('bookmark_folder:created', () => this.user.bookmark_folder_count++)
    this.$events.$on('bookmark_folder:removed', () => this.user.bookmark_folder_count--)

    this.$events.$on('pusher:user-invited', () => this.user.pending_invites_count++)
    this.$events.$on('pusher:user-cancelled', () => this.user.pending_invites_count--)

    this.checkOpenModal(this.$route.query)
  },
  methods: {
    fetchResearcher (id) {
      this.isLoaded = false

      ResearcherService.get(id).then(researcher => {
        this.researcher = researcher
        this.user = researcher.user || {}
        this.fetchHighlights()
        this.setSuggestions().then(this.fetchResearchCounts)

        // open connect modal
        if (this.$route.query.connect && this.$can.edit('researcher', this.user.rw_access)) this.openConnectModal()

        // Send mixPanel event when we're viewing someone elses profile
        if (researcher && researcher.user && researcher.user.rw_access !== 'owner') MPService.trackProfileVisit()
      }).catch(err => {
        // On 404 error, we redirect to search with the name as query
        if (err.status && err.status === 404) {
          const newQuery = (this.$route.query && this.$route.query.fullName) || id.replace(/-/g, ' ')

          this.$flash({ message: `Here you find all information we have on <b>${newQuery}</b>.` }, 'alert')

          this.$router.replace({
            name: 'searchHome',
            query: { keyword: `"${newQuery}"` }
          })
        } else {
          this.error = this.handleError(err)
          this.isLoaded = !this.isEmpty(this.error)
        }
      })
    },
    fetchHighlights () {
      SearchService.highlightsDetailView(this.getSearchQueryForHighlighting('researchers', this.researcher.slug_values)).then(highlights => {
        let parsedHighlights = this.generateHighlights(this.researcher, highlights, this.objectForHighlighting)
        for (let esProperty of this.objectForHighlighting.nameProperties) this.setForString(esProperty, esProperty)
        this.$set(this.researcher, 'highlights', parsedHighlights)
        this.isLoaded = true
      }).catch(err => {
        this.error = this.handleError(err)
      })
    },
    openConnectModal () {
      this.$events.$emit('modal:user:connect:open', this.$ls.getConnectData(), this.$route.query.connect)
    },
    setAsPublic (isPublic) {
      isPublic ? this.$can.setAsPublic(true) : this.$can.setAsPublic(false)
    },
    setSuggestions () {
      this.searchFilterObject.suggestion_indexes = this.suggestionIndexes
      this.searchFilterObject.values = this.researcher.slug_values
      this.searchFilterObject.label = this.researcher.full_name
      this.searchFilterObject.suggestions = [{
        type: 'person',
        must: this.getFullName(this.researcher),
        must_not: this.researcher.slug_values
      }]
      this.searchFilterObject.filter_order = [this.searchFilterObject.key]
      this.searchFilterObject.filters = {}
      this.searchFilterObject.filters[this.searchFilterObject.key] = {}
      this.searchFilterObject.filters[this.searchFilterObject.key].buckets = []
      this.searchFilterObject.filters[this.searchFilterObject.key].buckets.push({ active: true, values: this.searchFilterObject.values })

      return Promise.resolve()
    },
    fetchResearchCounts () {
      this.indexes = {}
      // Fetch counts for research of a RG to determine the default index we want to redirect to on "Research" click
      SearchService.counts(this.searchFilterObject).then(counts => {
        if (!counts || !counts.categories) return
        this.indexes = counts.categories
        const [index, indexData] = this.getMostRelevantIndex(counts.categories, 'publications')
        this.searchFilterObject.index = index
        this.hasResearch = indexData.total > 0
      }).catch(() => {
        // It's fine
      })
    }
  },
  destroyed () {
    this.$events.$off('user:updated')
    this.$events.$off('researcher:updated')
    this.$events.$off('bookmark_folder:created')
    this.$events.$off('bookmark_folder:removed')
    this.$events.$off('pusher:user-invited')
    this.$events.$off('pusher:user-cancelled')
    this.$events.$off('bookmark_folder:bookmarked')
    // this.$events.$off('profile:updated:all')
  }
}
</script>
